import { Component, OnInit, inject } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
  ValidatorFn,
  AbstractControl,
} from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { ILuminaria, IGeoJSONPoint, IGeoCache } from 'modelos/src';
import { HelperService } from '../../../../../auxiliares/servicios/helper.service';
import { LuminariasHttpService } from '../../../../../auxiliares/servicios/http/luminarias.service';
import { DireccionesService } from '../../../../../auxiliares/servicios/http/direcciones.service';

interface FilaCSV {
  fecha: string;
  nomElemento: string;
  puestaId: string;
  direccionMac: string;
  longitud: string;
  latitud: string;
}

@Component({
  selector: 'app-importar-ubicaciones',
  templateUrl: './importar-ubicaciones.component.html',
  styleUrl: './importar-ubicaciones.component.scss',
  standalone: false,
})
export class ImportarUbicacionesComponent implements OnInit {
  private fb = inject(UntypedFormBuilder);
  private dialogRef =
    inject<MatDialogRef<ImportarUbicacionesComponent>>(MatDialogRef);
  private helper = inject(HelperService);
  private luminariasService = inject(LuminariasHttpService);
  private geocodingService = inject(DireccionesService);

  public form?: UntypedFormGroup;
  public loading = false;
  public primerLineaEncabezado = true;

  // Separa el contenido del textarea en líneas
  public separarEnLineas(input: string): string[] | undefined {
    if (!input || input.trim() === '') {
      return undefined;
    }
    const lineas = input.split('\n').filter((linea) => linea.trim() !== '');
    return lineas;
  }

  // Parsea una línea CSV
  private parsearLinea(linea: string): FilaCSV | null {
    const campos = linea.split(',').map((c) => c.trim());

    if (campos.length < 6) {
      return null;
    }

    return {
      fecha: campos[0],
      nomElemento: campos[1],
      puestaId: campos[2],
      direccionMac: campos[3],
      longitud: campos[4],
      latitud: campos[5],
    };
  }

  // Validador personalizado para el textarea
  private validarTextarea(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const input: string = control.value;
      const lineas = this.separarEnLineas(input);

      if (!lineas) {
        return null;
      }

      for (let i = 0; i < lineas.length; i++) {
        const linea = lineas[i];
        const lineaParseada = this.parsearLinea(linea);

        if (!lineaParseada) {
          return { error: `Línea ${i + 1}: Formato incorrecto` };
        }

        // Validar que longitud y latitud sean números
        const lon = parseFloat(lineaParseada.longitud);
        const lat = parseFloat(lineaParseada.latitud);

        if (isNaN(lon) || isNaN(lat)) {
          return {
            error: `Línea ${i + 1}: Longitud o latitud no son números válidos`,
          };
        }

        // Validar rangos de coordenadas
        if (lon < -180 || lon > 180) {
          return {
            error: `Línea ${i + 1}: Longitud fuera de rango (-180 a 180)`,
          };
        }

        if (lat < -90 || lat > 90) {
          return {
            error: `Línea ${i + 1}: Latitud fuera de rango (-90 a 90)`,
          };
        }
      }

      return null;
    };
  }

  private createForm(): void {
    this.form = this.fb.group({
      csv: ['', [Validators.required, this.validarTextarea()]],
    });
  }

  public getErrorMessage(): string | undefined {
    if (this.form?.get('csv')?.hasError('error')) {
      return this.form?.get('csv')?.getError('error');
    }
    return undefined;
  }

  public close(): void {
    this.dialogRef.close();
  }

  // Procesa el CSV y actualiza las luminarias
  public async onSubmit(): Promise<void> {
    this.loading = true;
    try {
      const textArea = this.form?.get('csv')?.value;
      const lineas = this.separarEnLineas(textArea);

      if (!lineas || lineas.length === 0) {
        this.helper.notifWarn('No hay datos para procesar');
        this.loading = false;
        return;
      }

      let actualizadas = 0;
      let noEncontradas = 0;
      let errores = 0;
      let eliminadas = 0;

      // Obtener todas las luminarias
      const resultado = await this.luminariasService.getFiltered({
        limit: 0,
        includeChildren: true,
      });
      const luminarias = resultado.datos;

      for (const linea of lineas) {
        try {
          const fila = this.parsearLinea(linea);
          if (!fila) {
            errores++;
            continue;
          }

          // Buscar luminaria por deveui (que coincide con direccionMac)
          const luminaria = luminarias.find(
            (l: ILuminaria) =>
              l.deveui?.toLowerCase() === fila.direccionMac.toLowerCase(),
          );

          if (!luminaria) {
            noEncontradas++;
            continue;
          }

          // Buscar y eliminar cualquier otra luminaria que tenga el mismo puestaId en su identificacion
          const luminariaConMismoNombre = luminarias.find(
            (l: ILuminaria) =>
              l.identificacion === fila.puestaId && l._id !== luminaria._id,
          );

          if (luminariaConMismoNombre) {
            try {
              await this.luminariasService.delete(luminariaConMismoNombre._id!);
              eliminadas++;
            } catch (e) {
              console.error(
                `Error al eliminar luminaria con identificación ${fila.puestaId}`,
                e,
              );
            }
          }

          // Crear coordenadas [lng, lat]
          const coordenadas: [number, number] = [
            parseFloat(fila.longitud),
            parseFloat(fila.latitud),
          ];

          // Hacer geocoding reverso para obtener la dirección
          let direccion: string | undefined;
          try {
            // Luminarias → precisión 9.
            const resultado = await this.geocodingService.reverse(
              coordenadas,
              9,
            );

            direccion = this.formatDireccion(resultado);
          } catch (e) {
            console.warn(
              `No se pudo obtener dirección para ${fila.direccionMac}`,
              e,
            );
            // Continuar sin dirección si falla el geocoding
          }

          // Actualizar luminaria con ubicación, dirección e identificación
          // Crear GeoJSON para guardar
          const ubicacion: IGeoJSONPoint = {
            type: 'Point',
            coordinates: coordenadas,
          };

          await this.luminariasService.update(luminaria._id!, {
            ubicacion: ubicacion,
            ...(direccion && { direccion: direccion }),
            identificacion: fila.puestaId,
            tipoEnergizado: 'Nocturno',
          });

          actualizadas++;
        } catch (err) {
          console.error(`Error al procesar la línea: ${linea}`, err);
          errores++;
        }
      }

      // Mostrar resultados
      if (actualizadas > 0) {
        const mensaje = `Se actualizaron ${actualizadas} luminaria(s) correctamente.${
          eliminadas > 0 ? ` ${eliminadas} luminaria(s) eliminada(s).` : ''
        }${noEncontradas > 0 ? ` ${noEncontradas} no encontrada(s).` : ''}${errores > 0 ? ` ${errores} error(es).` : ''}`;

        this.helper.notifSuccess('Importación completada', mensaje);
        this.close();
      } else {
        this.helper.notifWarn(
          'No se actualizó ninguna luminaria',
          `${noEncontradas} no encontrada(s), ${errores} error(es)`,
        );
      }
    } catch (error) {
      console.error('Error en importación:', error);
      this.helper.notifError(error);
    } finally {
      this.loading = false;
    }
  }

  async ngOnInit(): Promise<void> {
    this.createForm();
  }

  private formatDireccion(result: IGeoCache): string {
    // Si tiene una propiedad 'direccion' directa
    if (result.direccion) {
      return result.direccion;
    }

    // Si tiene componentes separados (calle, numero, ciudad, etc.)
    const partes: string[] = [];
    if (result.partes.calle) partes.push(result.partes.calle);
    if (result.partes.numero) partes.push(result.partes.numero);
    if (result.partes.ciudad) partes.push(result.partes.ciudad);
    if (result.partes.provincia) partes.push(result.partes.provincia);
    if (result.partes.pais) partes.push(result.partes.pais);

    return partes.join(', ');
  }

  // Carga archivo CSV
  async cargarCsv(event: any): Promise<void> {
    const file = event.target.files[0];
    if (!file) return;

    let texto = await this.readFileContent(file);

    if (this.primerLineaEncabezado) {
      const lineas = this.separarEnLineas(texto);
      if (lineas) {
        lineas.shift();
        texto = lineas.join('\n');
      }
    }

    this.form?.patchValue({
      csv: texto,
    });
  }

  // Lee el contenido del archivo
  async readFileContent(file: File): Promise<string> {
    const reader = new FileReader();
    return new Promise((resolve, reject) => {
      reader.onload = (event) => resolve(event.target?.result as string);
      reader.onerror = (error) => reject(error);
      reader.readAsText(file);
    });
  }
}
